import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import debounce from 'lodash.debounce';
import { useState } from 'react';
import { DataGrid, FilterSearch } from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { FormBuilderForm } from '@repo/forms';
import { EventEmitter, constructQuery, sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { createForm, getForms, updateForm } from 'src/api/admin/forms';

interface FormsSearch {
	page?: number;
	search?: string;
	sort?: string;
}

export const Route = createFileRoute('/_admin/system/forms/')({
	validateSearch: (search: Record<string, unknown>): FormsSearch => ({
		page: search.page as number | undefined,
		search: search.search as string | undefined,
		sort: search.sort as string | undefined,
	}),
	loaderDeps: ({ search: { page, search, sort } }) => ({ page, search, sort }),
	loader: async ({ deps }) => {
		const queryParams = {
			page: deps.page,
			search: deps.search,
			sort: deps.sort || 'createdAt:desc',
			noPagination: 'false',
		};
		const query = constructQuery(queryParams);
		const formsResponse = await getForms(query);
		return { formsResponse };
	},
	component: FormsPage,
});

const columns = [
	{
		field: 'title',
		headerName: 'Title',
		flex: 2,
	},
	{
		field: 'slug',
		headerName: 'Slug',
		flex: 2,
	},
	{
		field: 'type',
		headerName: 'Type',
		flex: 1,
	},
];

function FormsPage() {
	const { page, search, sort } = Route.useSearch();
	const { formsResponse: initialFormsResponse } = Route.useLoaderData();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();

	const queryParams = {
		page,
		search,
		sort: sort || 'createdAt:desc',
		noPagination: 'false',
	};
	const query = constructQuery(queryParams);

	const { data: formsApiResponse, refetch: revalidateForms } = useQuery({
		queryKey: ['forms', query],
		queryFn: () => getForms(query),
		initialData: initialFormsResponse,
	});

	const [isAddFormDialogOpen, setIsAddFormDialogOpen] = useState(false);

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Forms');

	const handleOpenAddFormDialog = () => {
		setIsAddFormDialogOpen(true);
	};

	const handleCloseAddFormDialog = () => {
		setIsAddFormDialogOpen(false);
	};

	const handleRowClick = ({ row }: { row: { slug: string } }) => {
		navigate({ to: '/system/forms/$id', params: { id: row.slug } });
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page });
	};

	const handleSort = ([sortModel]: any[]) => {
		if (!sortModel) {
			return;
		}
		updateQueryParams({ sort: sortToString(sortModel) });
	};

	const actions = [
		<Button onClick={handleOpenAddFormDialog} startIcon={<AddIcon />} variant={'outlined'} key="add">
			Add Form
		</Button>,
	];

	const handleFormSubmit = async (formData: any) => {
		console.log('formData', formData);
		let isError;
		if (formData) {
			try {
				if (formData.id) {
					await updateForm(formData.id, formData);
				} else {
					await createForm(formData);
				}
				EventEmitter.emit('toast', {
					type: 'success',
					message: `Form saved`,
				});
			} catch (error) {
				console.error('Failed to save form:', error);
				isError = true;
				EventEmitter.emit('toast', {
					type: 'error',
					message: `Failed to save form. Did you enter all data correctly?`,
				});
			}
		}
		revalidateForms();
		queryClient.invalidateQueries({ queryKey: ['forms'] });
		if (!isError) {
			setIsAddFormDialogOpen(false);
		}
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	return (
		<>
			<GenericDataPageHeader actions={actions} />
			<div className="p-4 pt-0">
				<div key="search" className="w-[60%] flex-grow flex gap-2">
					<FilterSearch
						label="Search Form"
						defaultValue={search || ''}
						onChange={debounce((searchInput: string) => {
							handleSearchChange(searchInput);
						}, 350)}
						sx={{ width: '100%', minWidth: '40%', flexGrow: 1 }}
					/>
				</div>
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						data={formsApiResponse.data}
						page={formsApiResponse.page}
						pageSize={formsApiResponse.pageSize}
						pageSizeOptions={[formsApiResponse.pageSize]}
						pagination
						rowCount={formsApiResponse.count}
						columns={columns}
						paginationMode="server"
						sortingMode="server"
						onRowClick={handleRowClick}
						onSortModelChange={handleSort}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={[stringToSort(sort || 'createdAt:desc')]}
						onReload={revalidateForms}
						sortingOrder={['asc', 'desc']}
					/>
				</Paper>
			</div>
			<FormBuilderForm
				data={{}}
				isOpen={isAddFormDialogOpen}
				onClose={handleCloseAddFormDialog}
				onSubmit={handleFormSubmit}
			/>
		</>
	);
}
